import React, { useState } from 'react'
import { 
  Box, 
  Typography, 
  Paper, 
  TextField, 
  Button, 
  Alert, 
  Link as MuiLink,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material'
import { 
  Email, 
  Lock, 
  Visibility, 
  VisibilityOff,
  ArrowBack,
  CheckCircle 
} from '@mui/icons-material'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Auth } from 'aws-amplify'
import { Layout } from '@/components/Layout'
import { ForgotPasswordForm, ResetPasswordForm } from '@/types'

export default function ForgotPassword() {
  const navigate = useNavigate()
  const [step, setStep] = useState<'request' | 'reset'>('request')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [email, setEmail] = useState('')

  const {
    register: registerForgot,
    handleSubmit: handleSubmitForgot,
    formState: { errors: forgotErrors }
  } = useForm<ForgotPasswordForm>({ mode: 'onChange' })

  const {
    register: registerReset,
    handleSubmit: handleSubmitReset,
    formState: { errors: resetErrors },
    watch
  } = useForm<ResetPasswordForm>({ mode: 'onChange' })

  const newPassword = watch('newPassword')

  const onSubmitForgotPassword = async (data: ForgotPasswordForm) => {
    setIsLoading(true)
    setError(null)
    setSuccess(null)

    try {
      await Auth.forgotPassword(data.email)
      setEmail(data.email)
      setSuccess('Password reset code sent to your email. Please check your inbox.')
      setStep('reset')
    } catch (err: any) {
      console.error('Forgot password error:', err)
      
      switch (err.code) {
        case 'UserNotFoundException':
          setError('No account found with this email address.')
          break
        case 'InvalidParameterException':
          setError('Please enter a valid email address.')
          break
        case 'LimitExceededException':
          setError('Too many requests. Please try again later.')
          break
        default:
          setError('An error occurred. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const onSubmitResetPassword = async (data: ResetPasswordForm) => {
    setIsLoading(true)
    setError(null)
    setSuccess(null)

    try {
      await Auth.forgotPasswordSubmit(email, data.code, data.newPassword)
      setSuccess('Password reset successfully! You can now sign in with your new password.')
      
      // Redirect to login after successful reset
      setTimeout(() => {
        navigate('/auth/login')
      }, 2000)
    } catch (err: any) {
      console.error('Reset password error:', err)
      
      switch (err.code) {
        case 'CodeMismatchException':
          setError('Invalid verification code. Please check and try again.')
          break
        case 'ExpiredCodeException':
          setError('Verification code has expired. Please request a new one.')
          break
        case 'InvalidPasswordException':
          setError('Password does not meet requirements. Please ensure it has at least 8 characters with uppercase, lowercase, numbers, and special characters.')
          break
        default:
          setError('An error occurred during password reset. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const resendCode = async () => {
    try {
      await Auth.forgotPassword(email)
      setSuccess('New verification code sent to your email.')
      setError(null)
    } catch (err: any) {
      setError('Failed to resend verification code. Please try again.')
    }
  }

  if (step === 'reset') {
    return (
      <Layout showNavigation={false}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '70vh',
            px: 2,
          }}
        >
          <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <IconButton 
                onClick={() => setStep('request')}
                sx={{ mr: 1 }}
              >
                <ArrowBack />
              </IconButton>
              <Typography variant="h5" component="h1">
                Reset Password
              </Typography>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Enter the verification code sent to {email} and your new password.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmitReset(onSubmitResetPassword)} noValidate>
              <TextField
                {...registerReset('code', {
                  required: 'Verification code is required',
                  pattern: {
                    value: /^\d{6}$/,
                    message: 'Please enter a valid 6-digit code'
                  }
                })}
                fullWidth
                label="Verification Code"
                placeholder="Enter 6-digit code"
                margin="normal"
                error={!!resetErrors.code}
                helperText={resetErrors.code?.message}
                disabled={isLoading}
              />

              <TextField
                {...registerReset('newPassword', {
                  required: 'New password is required',
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters'
                  },
                  pattern: {
                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                    message: 'Password must contain uppercase, lowercase, number, and special character'
                  }
                })}
                fullWidth
                label="New Password"
                type={showNewPassword ? 'text' : 'password'}
                autoComplete="new-password"
                margin="normal"
                error={!!resetErrors.newPassword}
                helperText={resetErrors.newPassword?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle new password visibility"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                disabled={isLoading}
              />

              <TextField
                {...registerReset('confirmPassword', {
                  required: 'Please confirm your new password',
                  validate: (value) => value === newPassword || 'Passwords do not match'
                })}
                fullWidth
                label="Confirm New Password"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                margin="normal"
                error={!!resetErrors.confirmPassword}
                helperText={resetErrors.confirmPassword?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                disabled={isLoading}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, py: 1.5 }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Reset Password'
                )}
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Didn't receive the code?{' '}
                  <MuiLink
                    component="button"
                    type="button"
                    onClick={resendCode}
                    sx={{ textDecoration: 'none' }}
                  >
                    Resend
                  </MuiLink>
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Layout>
    )
  }

  return (
    <Layout showNavigation={false}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '70vh',
          px: 2,
        }}
      >
        <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%' }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Forgot Password?
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            No worries! Enter your email address and we'll send you a code to reset your password.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmitForgot(onSubmitForgotPassword)} noValidate>
            <TextField
              {...registerForgot('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Please enter a valid email address'
                }
              })}
              fullWidth
              label="Email Address"
              type="email"
              autoComplete="email"
              autoFocus
              margin="normal"
              error={!!forgotErrors.email}
              helperText={forgotErrors.email?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
              disabled={isLoading}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Send Reset Code'
              )}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <MuiLink component={Link} to="/auth/login" variant="body2" sx={{ textDecoration: 'none' }}>
                ← Back to Sign In
              </MuiLink>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Don't have an account?{' '}
                <MuiLink component={Link} to="/auth/register" sx={{ textDecoration: 'none' }}>
                  Sign up here
                </MuiLink>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Layout>
  )
}